import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { url } from 'projects/shared/src/lib/helpers/url';
import { ActivityBlockingService } from 'projects/shared/src/lib/services/activity-blocking.service';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { delay, map, mergeMap, retryWhen, tap } from 'rxjs/operators';
import { SharedSettings } from '../../../../../shared/src/lib/models/shared-settings';
import { LoadsSummary } from '../../models/shift-validation/loads-summary';

@Injectable({
    providedIn: 'root'
})
export class ShiftValidationService {
    private _currentLoadsSummary = new BehaviorSubject<LoadsSummary>(null);
    private _isValid = new BehaviorSubject<boolean>(true);

    subscriptions: Subscription[];
    siteId: string;
    date: DateTime;
    shiftId: number;
    shiftContextId: number;

    summaries = new Map<string, object>();

    constructor(
        private http: HttpClient,
        private settings: SharedSettings,
        private activityService: ActivityBlockingService) {

        this._currentLoadsSummary.next(null);
    }

    startCalculations(shiftContextId: number): Observable<void> {
        return this.http
            .post(this.settings.apiBaseUrl + url`WorkedShifts/${shiftContextId}/Validation/StartCalculations`,
                '{ "date": null }',
                { headers: { 'Content-Type': 'application/json' }})
            .pipe(map(_ => {}));
    }

    private withRetry<T>(observable: Observable<HttpResponse<T>>): Observable<T> {
        return observable.pipe(
            map(response => {
                if (response.status === 204) {
                    throw 204;
                }
                return response.body;
            }),
            retryWhen(result => result.pipe(mergeMap((r: Error | number) => {
                if (r === 204) {
                    return of(r).pipe(delay(3000));
                }
                return throwError(() => r);
            }))));
    }

    getLoadsSummary(shiftContextId: number): void {
        this._currentLoadsSummary.next(null);
        this.withRetry(this.http.get<LoadsSummary>(this.settings.apiBaseUrl +
            url`WorkedShifts/${shiftContextId}/Validation/Loads`,
            { observe: 'response' }))
            .subscribe(loadsSummary => {
                if (loadsSummary)
                    this._currentLoadsSummary.next(loadsSummary);
            });
    }

    isAuthNumberInUse(shiftContextId: number, authNumber: string): Observable<boolean> {
        return this.http.get<{ inUse: boolean }>(this.settings.apiBaseUrl
                + url`WorkedShifts/${shiftContextId}/Validation/InternalTransfers/inuse?authNumber=${authNumber}`)
            .pipe(map(result => result.inUse));
    }

    updateRlcTransferAuthNumbers(
        shiftContextId: number,
        rlcTransferAuthNumbers: { id: number, authNumber: string }[]
    ): Observable<any> {
        return this.http.put(this.settings.apiBaseUrl +
            url`WorkedShifts/${shiftContextId}/Validation/InternalTransfers`, rlcTransferAuthNumbers)
            .pipe(this.activityService.Wait('Saving Auth #s'));
    }

    preloadSummary(shiftContextId: number, route: string): Observable<void> {
        if (route)
            return this.withRetry(this.http.get<any>(
                this.settings.apiBaseUrl + url`WorkedShifts/${shiftContextId}/Validation/` + route,
                { observe: 'response' })
                .pipe(tap(x => {
                    this.mapSubinventory(route, x.body);
                })));
        else {
            return of(null);
        }
    }


    get CurrentLoadsSummary(): Observable<LoadsSummary> {
        return this._currentLoadsSummary.asObservable();
    }

    CurrentMappedSummary<T>(route: string): T {
        return this.summaries.get(route) as T;
    }

    private mapSubinventory(route: string, val: any): void {
        this.summaries.set(route, val);
    }

    reset(): void {
        this.summaries.clear();
        this._currentLoadsSummary.next(null);
    }

    get IsValid(): Observable<boolean> {
        return this._isValid.asObservable();
    }

    SetValid(val: boolean) {
        this._isValid.next(val);
    }
}
